import { useState, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";
import openSocket from "../../services/socket-io";

import { toast } from "react-toastify";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const useAuth = () => {
	const history = useHistory();
	const [isAuth, setIsAuth] = useState(false);
	const [loading, setLoading] = useState(true);
	// El backend responde 402 cuando la instalación está suspendida por mora.
	const [suspended, setSuspended] = useState(false);
	const [user, setUser] = useState({});
	const socketRef = useRef(null);
	const heartbeatRef = useRef(null);
	const userRef = useRef(user);

	// Keep userRef in sync with user state
	useEffect(() => {
		userRef.current = user;
	}, [user]);

	api.interceptors.request.use(
		config => {
			const token = localStorage.getItem("token");
			if (token) {
				let parsedToken;
				try {
					parsedToken = JSON.parse(token);
				} catch (err) {
					parsedToken = token;
				}
				config.headers["Authorization"] = `Bearer ${parsedToken}`;
				setIsAuth(true);
			}
			return config;
		},
		error => {
			Promise.reject(error);
		}
	);

	api.interceptors.response.use(
		response => {
			return response;
		},
		async error => {
			const originalRequest = error.config;
			if (error?.response?.status === 403 && !originalRequest._retry) {
				originalRequest._retry = true;

				const { data } = await api.post("/auth/refresh_token");
				if (data) {
					localStorage.setItem("token", JSON.stringify(data.token));
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
				}
				return api(originalRequest);
			}
			if (error?.response?.status === 402) {
				setSuspended(true);
			}
			if (error?.response?.status === 401) {
				localStorage.removeItem("token");
				api.defaults.headers.Authorization = undefined;
				setIsAuth(false);
			}
			return Promise.reject(error);
		}
	);

	useEffect(() => {
		const token = localStorage.getItem("token");
		(async () => {
			if (token) {
				try {
					const { data } = await api.post("/auth/refresh_token");
					api.defaults.headers.Authorization = `Bearer ${data.token}`;
					setIsAuth(true);
					setUser(data.user);
				} catch (err) {
					toastError(err);
				}
			}
			setLoading(false);
		})();
	}, []);

	// Single socket connection when authenticated
	useEffect(() => {
		if (!isAuth) {
			if (heartbeatRef.current) {
				clearInterval(heartbeatRef.current);
				heartbeatRef.current = null;
			}
			if (socketRef.current) {
				socketRef.current.off();
				socketRef.current.disconnect();
				socketRef.current = null;
			}
			return;
		}

		const socket = openSocket();
		socketRef.current = socket;

		const handleUser = data => {
			if (data.action === "update" && data.user.id === userRef.current.id) {
				setUser(data.user);
			}
		};

		const handleUserStatus = data => {
			setUser(prev => {
				if (prev && prev.id === data.userId) {
					return { ...prev, isOnline: data.isOnline, lastSeenAt: data.lastSeenAt };
				}
				return prev;
			});
		};

		const handleTaskReminder = data => {
			if (data.action === "reminder") {
				toast.warning(`⏰ Recordatorio: ${data.task.title} (Cliente: ${data.task.contact?.name || "Sin nombre"})`, {
					autoClose: 10000
				});
			}
		};

		socket.on("user", handleUser);
		socket.on("userStatus", handleUserStatus);
		socket.on("taskReminder", handleTaskReminder);

		heartbeatRef.current = setInterval(() => {
			if (socketRef.current && socketRef.current.connected) {
				socketRef.current.emit("heartbeat");
			}
		}, 30000);

		return () => {
			if (heartbeatRef.current) {
				clearInterval(heartbeatRef.current);
				heartbeatRef.current = null;
			}
			socket.off("user", handleUser);
			socket.off("userStatus", handleUserStatus);
			socket.off("taskReminder", handleTaskReminder);
			socketRef.current = null;
		};
	}, [isAuth]);

	const handleLogin = async userData => {
		setLoading(true);

		try {
			const { data } = await api.post("/auth/login", userData);
			localStorage.setItem("token", JSON.stringify(data.token));
			api.defaults.headers.Authorization = `Bearer ${data.token}`;
			setUser(data.user);
			setIsAuth(true);
			toast.success(i18n.t("auth.toasts.success"));
			history.push("/tickets");
			setLoading(false);
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	const handleLogout = async () => {
		setLoading(true);

		try {
			await api.delete("/auth/logout");
			setIsAuth(false);
			setUser({});
			localStorage.removeItem("token");
			api.defaults.headers.Authorization = undefined;
			setLoading(false);
			history.push("/login");
		} catch (err) {
			toastError(err);
			setLoading(false);
		}
	};

	return { isAuth, user, loading, suspended, handleLogin, handleLogout };
};

export default useAuth;
